import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { App as AntApp, Input, Button, Checkbox } from 'antd';
import { AsyncButton } from '../components/kit';
import SignaturePad from '../components/SignaturePad';
import * as formsApi from '../api/bookingForms';

// PUBLIC /sign/:token — a customer (purchaser/tenant/owner/landlord/witness) opens the
// emailed link on their OWN device. No agent login: access = single-use token + email OTP.
// Flow: validate link → request & enter 6-digit OTP → read the rendered document →
// give / confirm name + NRIC → draw signature → submit (server stamps the signed datetime).
//
// TWO KINDS OF LINK ARRIVE HERE, and the difference is the whole point of the
// sequential fill & sign flow Leon asked for (2026-09-04):
//   · the agent already named this party — the page CONFIRMS a prefilled name, which
//     is all this page ever used to do;
//   · the party slot is still empty (`can_fill_party && !party_slot_filled`) — the
//     person reading this IS the name, so the box starts empty and asks for it. It is
//     never prefilled from `signatory_label`: an unfilled slot's token stores the
//     LABEL ("Purchaser (1)") because the column is NOT NULL, and echoing that back
//     is exactly the bug this flow was built to fix. The server sends
//     `signatory_name` read live from the document instead, null when unfilled.
// `counterparty_hidden` says the other half of the agreement has been redacted out
// of the copy below — normal here, not an error, so it is said plainly and calmly.
export default function PublicSigningPage() {
  const { token } = useParams();
  const { message } = AntApp.useApp();

  const [phase, setPhase] = useState('loading'); // loading | otp | review | done | error
  const [error, setError] = useState(null);
  const [emailHint, setEmailHint] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [info, setInfo] = useState(null); // { signatory_*, form_ref, form_title, preview_html }
  const [showDoc, setShowDoc] = useState(false);
  const [docZoom, setDocZoom] = useState(1);
  const [confirmName, setConfirmName] = useState('');
  const [confirmNric, setConfirmNric] = useState('');
  const [nameErr, setNameErr] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  // An agreement can carry an Inventory List and — on the owner's side — their
  // Letter of Undertaking (Leon Lim, 4 Sep 2026). One signature goes onto all of
  // them, so each is ticked as read first. `documents` comes from the server, in page
  // order. Mirrored by the admin portal's PublicSigningPage.
  const [ticked, setTicked] = useState({});
  const frameRef = useRef(null);
  const docs = info?.documents || [];
  const multi = docs.length > 1;
  const allTicked = docs.every((d) => ticked[d.key]);
  const allDocs = docs.length === 2 ? 'both documents' : `all ${docs.length} documents`;
  // Each entry carries `page`, the index of its first `.page` block in the frame. It
  // is not the list position: a witness's copy still prints the Inventory List
  // between the agreement and the LOU, yet the list is not one of theirs to sign.
  const openDoc = (d, i) => {
    setShowDoc(true);
    setTimeout(() => {
      const pages = frameRef.current?.contentDocument?.querySelectorAll('.page');
      pages?.[d.page ?? i]?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      frameRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 120);
  };

  // This link may write its holder's name into a party slot that is still empty —
  // so the page ASKS for the name rather than asking them to check one. Once the
  // agent has named the party (or the link never had the grant) it is today's
  // confirm-what-is-there behaviour, unchanged.
  const selfFill = !!info?.can_fill_party && !info?.party_slot_filled;

  useEffect(() => {
    formsApi.getPublicSign(token)
      .then((r) => {
        const d = r?.data ?? r;
        if (d.requires_otp) {
          setEmailHint(d.email_hint || '');
          setPhase('otp');
        } else {
          setInfo(d);
          // `signatory_name` is read LIVE from the document and is null while the
          // party slot is empty, so this leaves the box blank rather than seeding it
          // with anything. Never fall back to `signatory_label` here.
          setConfirmName(d.signatory_name || '');
          setConfirmNric(d.signatory_nric || '');
          setPhase('review');
        }
      })
      .catch((e) => { setError(e.message); setPhase('error'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const sendOtp = async () => {
    const r = await formsApi.createPublicSignResendOtp(token);
    const d = r?.data ?? r;
    setEmailHint(d.email_hint || emailHint);
    setOtpSent(true);
    message.success('Verification code sent to your email');
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) { message.warning('Enter the 6-digit code from your email'); return; }
    const r = await formsApi.createPublicSignVerifyOtp(token, { otp_code: otp });
    const d = r?.data ?? r;
    setInfo(d);
    // Blank when the party slot has not been filled in — see above.
    setConfirmName(d.signatory_name || '');
    setConfirmNric(d.signatory_nric || '');
    setPhase('review');
  };

  const submit = async (dataUrl) => {
    // Blocked here as well as marked on the field: the server refuses a self-filled
    // slot with no name (400), and a signature drawn and then rejected by the API is
    // a signature the person has to draw again.
    if (multi && !allTicked) {
      message.warning('Tick each document to confirm you have read it');
      return;
    }
    if (!confirmName.trim()) {
      setNameErr(true);
      message.warning(selfFill
        ? `Enter your full name first — it is what goes onto the document as the ${info.signatory_label || 'signing party'}`
        : 'Please confirm your full name');
      return;
    }
    const r = await formsApi.createPublicSignSubmit(token, {
      signature_data_url: dataUrl,
      confirmed_name: confirmName.trim(),
      confirmed_nric: confirmNric.trim() || undefined,
    });
    const d = r?.data ?? r;
    setSignedAt(d.signed_at || new Date().toISOString());
    setPhase('done');
  };

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', paddingTop: 'calc(14px + env(safe-area-inset-top))', background: 'linear-gradient(135deg,#241854,#18345E)', color: '#fff', flex: 'none' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>CID Realtors</div>
        <div style={{ fontSize: 11.5, opacity: 0.8 }}>Secure document e-signing</div>
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 560, width: '100%', margin: '0 auto' }}>
        {phase === 'loading' && <Card><p style={muted}>Checking your signing link…</p></Card>}

        {phase === 'error' && (
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--bad)' }}>This link can’t be used</div>
            <p style={{ ...muted, marginTop: 6 }}>{error}</p>
            <p style={{ ...muted, marginTop: 6 }}>Ask your agent to send a new signing link.</p>
          </Card>
        )}

        {/* ── Step 1 · email OTP verification ── */}
        {phase === 'otp' && (
          <>
            <Card>
              <div style={{ fontSize: 14.5, fontWeight: 700 }}>Verify it’s you</div>
              <p style={{ ...muted, marginTop: 6 }}>
                To protect this document, we’ll send a 6-digit verification code to your email
                {emailHint ? <> <b style={{ color: 'var(--text)' }}>{emailHint}</b></> : null}.
              </p>
              {!otpSent ? (
                <AsyncButton type="primary" block onClick={sendOtp} onError={(e) => message.error(e.message)} style={{ marginTop: 12 }}>
                  Email me the code
                </AsyncButton>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                  <Input
                    size="large" inputMode="numeric" maxLength={6} value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit code"
                    style={{ textAlign: 'center', fontSize: 22, letterSpacing: 10, fontVariantNumeric: 'tabular-nums' }}
                    onPressEnter={verifyOtp}
                  />
                  <AsyncButton type="primary" block onClick={verifyOtp} onError={(e) => message.error(e.message)}>Verify code</AsyncButton>
                  <AsyncButton type="text" block onClick={sendOtp} onError={(e) => message.error(e.message)} style={{ color: 'var(--muted)' }}>
                    Resend code
                  </AsyncButton>
                </div>
              )}
            </Card>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', textAlign: 'center' }}>The code expires in 10 minutes · this link expires 24h after it was sent</div>
          </>
        )}

        {/* ── Step 2 · review document + sign ── */}
        {phase === 'review' && info && (
          <>
            <Card>
              <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.07em', color: 'var(--muted)' }}>
                {multi ? `YOU ARE SIGNING ${docs.length} DOCUMENTS` : 'YOU ARE SIGNING'}
              </div>
              {multi ? (
                <div data-demo="sign-docs" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  {docs.map((d, i) => (
                    <button key={d.key} type="button" onClick={() => openDoc(d, i)} style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface2)' }}>
                      <span style={{ width: 22, height: 22, borderRadius: 50, background: 'var(--chip)', color: 'var(--primary)', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, flex: 'none' }}>{i + 1}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{d.title}</span>
                        {d.ref && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', fontFamily: 'ui-monospace,monospace' }}>{d.ref}</span>}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600 }}>Read</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 15, fontWeight: 700, marginTop: 3 }}>{info.form_title}</div>
              )}
              {!multi && <div style={{ fontSize: 12.5, color: 'var(--muted)', fontFamily: 'ui-monospace,monospace', marginTop: 2 }}>{info.form_ref}</div>}
              {/* Which party of the document this link speaks for. On an agreement
                  both sides get the same letter through the same page, so saying it
                  out loud is how someone knows they were sent the right one. */}
              {info.signatory_label && (
                <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 6 }}>
                  as the <b style={{ color: 'var(--text)' }}>{info.signatory_label}</b>
                </div>
              )}
              <Button block style={{ marginTop: 12 }} onClick={() => setShowDoc((s) => !s)}>
                {showDoc ? (multi ? 'Hide documents' : 'Hide document') : (multi ? 'Read all documents' : 'Read the document')}
              </Button>
            </Card>

            {/* The other side is redacted out of this copy. Stated in the app's own
                muted surface rather than as a warning: this is how the flow is
                supposed to work, and a customer who reads it as an error stops. */}
            {info.counterparty_hidden && (
              <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 14, padding: '13px 15px', display: 'flex', gap: 11 }}>
                <span style={{ flex: 'none', width: 20, height: 20, borderRadius: 50, background: 'var(--chip)', color: 'var(--primary)', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, marginTop: 1 }}>i</span>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.55 }}>
                  {/* Only what the flow guarantees: the agent passes it on, when is theirs to decide.
                      With `both_hidden` the two sides are kept apart for the whole agreement —
                      this signer is hidden on the other copy too, and the other side may have
                      signed already — so the hand-over sentence would be false twice over. */}
                  {info.both_hidden ? (
                    <>
                      The {info.counterparty_label || 'other party'}&rsquo;s details are kept private on your copy, and your details are kept private on theirs.
                      Your agent has the complete agreement.
                    </>
                  ) : (
                    <>
                      The {info.counterparty_label || 'other party'}&rsquo;s details are not shown on your copy.
                      Your agent will pass the document to the {info.counterparty_label || 'other party'} once you have signed; your details will be on their copy.
                    </>
                  )}
                </div>
              </div>
            )}

            {showDoc && (
              <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                {/* The app viewport disables pinch-zoom, so − / + scale the iframe instead
                    (narrower CSS viewport scaled up; the document pans inside the frame). */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '8px 10px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
                  <Button size="small" onClick={() => setDocZoom((z) => Math.max(1, Math.round(z / 1.25 * 100) / 100))} disabled={docZoom <= 1} style={{ width: 32, padding: 0 }}>−</Button>
                  <Button size="small" onClick={() => setDocZoom((z) => Math.min(3, Math.round(z * 1.25 * 100) / 100))} disabled={docZoom >= 3} style={{ width: 32, padding: 0 }}>+</Button>
                </div>
                <div style={{ height: '62vh', overflow: 'hidden' }}>
                  <iframe
                    ref={frameRef}
                    data-demo="sign-frame"
                    title="Document"
                    srcDoc={info.preview_html}
                    /* Same-origin but still no scripts, so "Read" can scroll the frame to a
                       document's page; the document itself can run nothing. */
                    sandbox="allow-same-origin"
                    style={{ width: `${100 / docZoom}%`, height: `${100 / docZoom}%`, transform: `scale(${docZoom})`, transformOrigin: '0 0', border: 'none', display: 'block', background: '#fff' }}
                  />
                </div>
              </div>
            )}

            <Card>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {selfFill ? 'Your details' : 'Confirm your identity'}
              </div>
              <p style={{ ...muted, fontSize: 12.5, marginTop: 5 }}>
                {selfFill
                  ? 'Your name is not on this document yet. Enter it exactly as it should be printed and signed — it goes onto the agreement as you type it.'
                  : 'Check that these are right before you sign.'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
                <div>
                  {/* Marked required only on a slot this link fills: on a confirm
                      link the name is already on the document either way. */}
                  <div style={lbl}>
                    {selfFill && <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>}
                    {selfFill ? 'Your full name (as per IC)' : 'Full name (as per IC)'}
                  </div>
                  <Input
                    value={confirmName}
                    status={nameErr && !confirmName.trim() ? 'error' : undefined}
                    onChange={(e) => { setConfirmName(e.target.value); if (nameErr) setNameErr(false); }}
                    placeholder="Your full name"
                  />
                  {nameErr && !confirmName.trim() && (
                    <div style={{ fontSize: 11.5, color: 'var(--bad)', marginTop: 5 }}>
                      Your full name is required before you can sign.
                    </div>
                  )}
                </div>
                <div>
                  <div style={lbl}>NRIC / passport no (optional)</div>
                  <Input value={confirmNric} onChange={(e) => setConfirmNric(e.target.value)} placeholder="e.g. 900101-14-5678" />
                </div>
              </div>
            </Card>

            {multi && (
              <Card>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Read before you sign</div>
                <div data-demo="sign-ticks" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                  {docs.map((d, i) => (
                    <Checkbox key={d.key} data-demo={`tick-${d.key}`} checked={!!ticked[d.key]} onChange={(e) => setTicked((t) => ({ ...t, [d.key]: e.target.checked }))}>
                      <span style={{ fontSize: 13 }}>I have read the <b>{d.title}</b></span>
                      <a onClick={(e) => { e.preventDefault(); openDoc(d, i); }} style={{ marginLeft: 6, fontSize: 12 }}>open</a>
                    </Checkbox>
                  ))}
                </div>
              </Card>
            )}

            <Card>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: multi ? 4 : 10 }}>Draw your signature</div>
              {multi && (
                <div data-demo="sign-once" style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 10 }}>
                  One signature — it is placed on {allDocs}: {docs.map((d) => d.title).join(', ')}.
                </div>
              )}
              <SignaturePad onConfirm={submit} />
              <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 10, lineHeight: 1.5 }}>
                By tapping “Sign &amp; submit” you agree that this electronic signature is your legal
                signature on {multi ? `${info.form_ref} and the documents listed above` : info.form_ref}, and that the date and time of signing will be recorded.
              </p>
            </Card>
          </>
        )}

        {/* ── Step 3 · done ── */}
        {phase === 'done' && (
          <Card>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'color-mix(in srgb,var(--ok) 15%,transparent)', color: 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '4px auto 0' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 13 10 18 19 7" /></svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, textAlign: 'center', marginTop: 10 }}>Signed successfully</div>
            <p style={{ ...muted, textAlign: 'center', marginTop: 6 }}>
              Your signature was recorded{signedAt ? <> on <b style={{ color: 'var(--text)' }}>{new Date(signedAt).toLocaleString()}</b></> : null}{multi ? <> — on {allDocs}</> : null}.
              You can close this page — your agent has been updated.
            </p>
          </Card>
        )}

        <div style={{ fontSize: 11.5, color: 'var(--muted)', textAlign: 'center', paddingBottom: 10 }}>
          CID Realtors Sdn Bhd · secure e-signing
        </div>
      </div>
    </div>
  );
}

// Signature pad (mouse + touch) — same behaviour as the agent-side pad in FormDetailPage.
const Card = ({ children }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px' }}>{children}</div>
);
const muted = { fontSize: 13, color: 'var(--muted)', lineHeight: 1.55, margin: 0 };
const lbl = { fontSize: 11.5, color: 'var(--muted)', marginBottom: 5 };
